export const SCORING_TYPE_VALUES = [
  'time',
  'reps',
  'rounds',
  'rounds_reps',
  'metres',
  'cals',
  'distance',
  'calories',
  'points',
] as const;

export type ScoringType = (typeof SCORING_TYPE_VALUES)[number];
export type ScoringDirection = 'ascending' | 'descending';

/**
 * Scoring types accepted when defining programming.
 *
 * This is intentionally separate from SCORING_TYPE_VALUES: score entry still
 * accepts legacy stored types and does not yet support every programming type.
 */
export const PROGRAMMING_SCORING_TYPE_VALUES = [
  'time',
  'reps',
  'rounds',
  'rounds_reps',
  'points',
  'distance',
  'calories',
  'shots',
] as const;

export type ProgrammingScoringType = (typeof PROGRAMMING_SCORING_TYPE_VALUES)[number];

export const PROGRAMMING_SCORING_TYPE_METADATA = {
  time: { label: 'For Time', direction: 'ascending', selectable: true },
  reps: { label: 'For Reps', direction: 'descending', selectable: true },
  rounds: { label: 'Rounds + Reps', direction: 'descending', selectable: false },
  rounds_reps: { label: 'Rounds + Reps', direction: 'descending', selectable: true },
  points: { label: 'Points', direction: 'descending', selectable: false },
  distance: { label: 'For Distance', direction: 'descending', selectable: true },
  calories: { label: 'For Calories', direction: 'descending', selectable: true },
  shots: { label: 'For Shots', direction: 'descending', selectable: true },
} as const satisfies Record<
  ProgrammingScoringType,
  { label: string; direction: ScoringDirection; selectable: boolean }
>;

export const PROGRAMMING_SELECTABLE_SCORING_TYPE_VALUES = [
  'time',
  'rounds_reps',
  'reps',
  'distance',
  'calories',
  'shots',
] as const satisfies readonly ProgrammingScoringType[];

export const PROGRAMMING_SCORING_TYPE_OPTIONS = PROGRAMMING_SELECTABLE_SCORING_TYPE_VALUES.map(
  (value) => ({
    value,
    label: PROGRAMMING_SCORING_TYPE_METADATA[value].label,
  })
);

/**
 * The Content library also defines legacy rounds- and points-based workouts.
 * Keep this preset in the shared contract so the form does not own another
 * scoring-type list.
 */
export const PROGRAMMING_CONTENT_SCORING_TYPE_VALUES = [
  'time',
  'reps',
  'rounds',
  'points',
  'calories',
  'distance',
] as const satisfies readonly ProgrammingScoringType[];

export const PROGRAMMING_CONTENT_SCORING_TYPE_OPTIONS = PROGRAMMING_CONTENT_SCORING_TYPE_VALUES.map(
  (value) => ({
    value,
    label: PROGRAMMING_SCORING_TYPE_METADATA[value].label,
  })
);

export function getProgrammingScoringLabel(scoringType: string): string {
  return (
    PROGRAMMING_SCORING_TYPE_METADATA[scoringType as ProgrammingScoringType]?.label ?? scoringType
  );
}

export function getProgrammingScoringDirection(scoringType: string): ScoringDirection {
  return (
    PROGRAMMING_SCORING_TYPE_METADATA[scoringType as ProgrammingScoringType]?.direction ??
    'descending'
  );
}

export type WorkoutAttributionSource = 'hq' | 'agent' | 'affiliate';

export type WorkoutAttribution = {
  source: WorkoutAttributionSource;
  name: string;
};

export type Track = {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
};

export type TrackCreate = Omit<Track, 'id' | 'created_at'>;

export type TrackUpdate = Partial<Omit<Track, 'id' | 'created_at'>>;

export type Workout = {
  id: string;
  track_id: string;
  name: string;
  description: string | null;
  movements: string | null;
  scoring_type: ScoringType;
  scoring_direction: ScoringDirection;
  equipment: string | null;
  scheduled_date: string | null;
  created_by: string | null;
  created_by_username?: string | null;
  attribution?: WorkoutAttribution | null;
  created_at: string;
  updated_at: string;
};

export type WorkoutCreate = Omit<Workout, 'id' | 'created_at' | 'updated_at'>;

export type WorkoutUpdate = Partial<Omit<Workout, 'id' | 'created_at' | 'updated_at'>>;

export type WorkoutWithTrack = Workout & {
  track: {
    id: string;
    name: string;
  };
};
